import { QueryResult } from "@/api/utils/types"
import { useQuery } from "@/api/utils/useQuery"

import { AggregatedQueryProps } from "@/api/utils/QueryAggregator"
import { mapQueryResult } from "@/api/utils/mapQuery"
import { IMeterpointConsent } from "@/models/meterpointConsent/meterpointConsent.types"
import {
  ConsentDefinition,
  ConsentResponse,
  ConsentSetting,
} from "@/api/backofficeRest/generated/model"
import { MeterpointConsentParser } from "@/models/meterpointConsent/meterpointConsent.parser"
import dayjs from "dayjs"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"

export interface IConsentsService {
  getMeterpointConsents(
    accountId: number | null,
  ): QueryResult<IMeterpointConsent[]>
  getMeterpointConsentById(
    accountId: number | null,
    meterpointId: string,
  ): AggregatedQueryProps<IMeterpointConsent[] | null>
}

class ConsentsServiceClass implements IConsentsService {
  getMeterpointConsents(
    accountId: number | null,
  ): QueryResult<IMeterpointConsent[]> {
    return useQuery({
      ...BackofficeRestQueries.meterpoints.consents(accountId ?? -1),
      placeholderData: [],
      useRawResponse: true,
      enabled: !!accountId,
      parserFn: (rawData: ConsentResponse[]) =>
        (rawData ?? []).map((consent) =>
          MeterpointConsentParser.toDomain(consent),
        ),
    })
  }

  getPrioritisedMeterpointConsents(
    accountId: number | null,
  ): AggregatedQueryProps<IMeterpointConsent[]> {
    return mapQueryResult(this.getMeterpointConsents(accountId), (query) => {
      const tomorrow = dayjs().add(1, "day").startOf("day")
      const acc: Record<string, (typeof query.data)[number]> = {}

      for (const item of query.data) {
        const key = item.consentDefinition
        const current = acc[key]

        if (!current) {
          acc[key] = item
          continue
        }

        const itemStartDate = dayjs(item.startDate).startOf("day")
        const currentStartDate = dayjs(current.startDate).startOf("day")

        if (itemStartDate.isSame(tomorrow)) {
          acc[key] = item
        } else if (!currentStartDate.isSame(tomorrow)) {
          // If neither is the priority date → keep the earliest
          acc[key] = itemStartDate.isBefore(currentStartDate) ? item : current
        }
      }

      return Object.values(acc)
    })
  }

  getMeterpointConsentById(
    accountId: number | null,
    meterpointIdentifier: string,
  ): AggregatedQueryProps<IMeterpointConsent[]> {
    return mapQueryResult(this.getMeterpointConsents(accountId), (query) => {
      if (
        query.isError &&
        query.error?.message ===
          "Cannot view or change consents for TOU tariffs"
      ) {
        return [
          {
            consentDefinition: ConsentDefinition.MPAN_BILLING,
            setting: ConsentSetting.HALF_HOURLY,
            meterPointIdentifier: meterpointIdentifier,
            startDate: dayjs(),
            endDate: undefined,
          },
          {
            consentDefinition: ConsentDefinition.MPAN_SETTLEMENT,
            setting: ConsentSetting.HALF_HOURLY,
            meterPointIdentifier: meterpointIdentifier,
            startDate: dayjs(),
            endDate: undefined,
          },
          {
            consentDefinition: ConsentDefinition.MPRN_BILLING,
            setting: ConsentSetting.HALF_HOURLY,
            meterPointIdentifier: meterpointIdentifier,
            startDate: dayjs(),
            endDate: undefined,
          },
        ]
      }

      return query.data?.filter(
        (consent) => consent.meterPointIdentifier === meterpointIdentifier,
      )
    })
  }
}

export const ConsentsService = new ConsentsServiceClass()
